import React from 'react';
import {
  Box,
  Text,
  SimpleGrid,
  FormControl,
  FormLabel,
  Select,
  Switch,
  HStack,
} from '@chakra-ui/react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { InputForm } from '../../../../../components/inputs/forms/InputForm';
import { TextAreaInputForm } from '../../../../../components/inputs/forms/TextAreaInputForm';
import { CnaeSelector } from './CnaeSelector';
import { IWizardFormValues } from '../schema';

// Switch de flag do dados_scp (Chakra Switch + Controller).
const ScpSwitch = ({ name, label, help }: { name: string; label: string; help?: string }) => {
  const { control } = useFormContext<IWizardFormValues>();
  return (
    <Controller
      control={control}
      name={name as keyof IWizardFormValues}
      render={({ field }) => (
        <FormControl>
          <HStack align="flex-start" spacing={2}>
            <Switch
              isChecked={!!field.value}
              onChange={e => field.onChange(e.target.checked)}
              mt={1}
            />
            <Box>
              <FormLabel mb={0} fontWeight="semibold" fontSize="sm">
                {label}
              </FormLabel>
              {help && (
                <Text fontSize="xs" color="gray.500">
                  {help}
                </Text>
              )}
            </Box>
          </HStack>
        </FormControl>
      )}
    />
  );
};

/**
 * Painel de configuração da SCP (natureza='scp'). Escreve em `dados_scp.*`
 * (JSONB no backend). Renderizado só quando natureza=scp (ver StepEmpresa).
 */
export const ScpConfigPanel = () => {
  const { control } = useFormContext<IWizardFormValues>();
  const objetoModo = useWatch({ control, name: 'dados_scp.objeto_modo' as any });
  const prazoTipo = useWatch({ control, name: 'dados_scp.prazo_tipo' as any });
  const confid = useWatch({ control, name: 'dados_scp.confidencialidade' as any });
  const alcada = useWatch({ control, name: 'dados_scp.limite_alcada' as any });
  const mediacao = useWatch({ control, name: 'dados_scp.mediacao_previa' as any });
  const distr = useWatch({ control, name: 'dados_scp.distribuicao_desproporcional' as any });

  return (
    <Box mt={6} pt={6} borderTopWidth="1px" borderColor="purple.200">
      <Text fontSize="sm" fontWeight="bold" mb={1} color="purple.600">
        Configuração da SCP (Sociedade em Conta de Participação)
      </Text>
      <Text fontSize="xs" color="gray.500" mb={4}>
        A SCP não registra na Junta (sem personalidade jurídica, art. 993) e identifica-se pelo nome
        do sócio ostensivo. O CNPJ é obtido a partir deste instrumento, vinculado ao ostensivo.
      </Text>

      {/* Objeto — livre (projeto) ou CNAE */}
      <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
        <Controller
          control={control}
          name={'dados_scp.objeto_modo' as any}
          render={({ field }) => (
            <FormControl mb={0}>
              <FormLabel fontWeight="bold" mb={1}>
                Objeto — modo
              </FormLabel>
              <Select {...field} value={field.value ?? 'livre'}>
                <option value="livre">Texto livre (projeto/empreendimento)</option>
                <option value="cnae">CNAEs (atividades econômicas)</option>
              </Select>
            </FormControl>
          )}
        />
      </SimpleGrid>
      <Box mt={3}>
        {objetoModo === 'cnae' ? (
          <CnaeSelector />
        ) : (
          <TextAreaInputForm
            name="objeto_social"
            label="Objeto da SCP — descrição do projeto/empreendimento (ex.: a produção e realização do evento X)"
          />
        )}
      </Box>

      {/* Capital + prazo */}
      <SimpleGrid columns={{ base: 1, md: 2 }} gap={3} mt={4}>
        <ScpSwitch
          name="dados_scp.capital_nominal"
          label="Capital nominal (simbólico, sem aporte)"
          help="Quotas nominais sem integralização (ex.: 50.000 quotas de R$ 1,00). Desligue para capital real."
        />
        <Controller
          control={control}
          name={'dados_scp.prazo_tipo' as any}
          render={({ field }) => (
            <FormControl mb={0}>
              <FormLabel fontWeight="bold" mb={1}>
                Prazo de duração
              </FormLabel>
              <Select {...field} value={field.value ?? 'determinado'}>
                <option value="determinado">Determinado (projeto/evento)</option>
                <option value="indeterminado">Indeterminado</option>
              </Select>
            </FormControl>
          )}
        />
      </SimpleGrid>
      {prazoTipo !== 'indeterminado' && (
        <SimpleGrid columns={{ base: 1, md: 3 }} gap={3} mt={3}>
          <InputForm
            name="dados_scp.prazo_duracao_valor"
            label="Duração"
            type="number"
            controlMb={0}
          />
          <Controller
            control={control}
            name={'dados_scp.prazo_duracao_unidade' as any}
            render={({ field }) => (
              <FormControl mb={0}>
                <FormLabel fontWeight="bold" mb={1}>
                  Unidade
                </FormLabel>
                <Select {...field} value={field.value ?? 'meses'}>
                  <option value="dias">dias</option>
                  <option value="meses">meses</option>
                  <option value="anos">anos</option>
                </Select>
              </FormControl>
            )}
          />
          <InputForm
            name="dados_scp.prazo_condicao_termino"
            label="Condição de término (opcional)"
            controlMb={0}
            helper="Ex.: até a conclusão do projeto"
          />
        </SimpleGrid>
      )}

      {/* Distribuição + deliberações. Cada switch com valor fica numa célula
          (valor empilhado abaixo) — alinhamento independe de quais estão on. */}
      <SimpleGrid columns={{ base: 1, md: 2 }} gap={3} mt={4} alignItems="start">
        <Box>
          <ScpSwitch
            name="dados_scp.distribuicao_desproporcional"
            label="Distribuição desproporcional (via acordo)"
            help="Padrão: proporcional às quotas. Ligue para permitir formas desproporcionais (art. 1.007)."
          />
          {distr && (
            <Box mt={2}>
              <InputForm
                name="dados_scp.distribuicao_prazo_pagamento"
                label="Prazo de pagamento (opcional)"
                controlMb={0}
                helper="Ex.: 10 (dez) dias úteis após a aprovação das contas"
              />
            </Box>
          )}
        </Box>
        <InputForm
          name="dados_scp.deliberacoes_quorum"
          label="Quórum das deliberações (opcional)"
          controlMb={0}
          helper="Padrão: maioria do capital social"
        />
      </SimpleGrid>

      {/* Cláusulas opcionais */}
      <Text fontSize="sm" fontWeight="bold" mt={5} mb={2} color="gray.700">
        Cláusulas opcionais
      </Text>
      <SimpleGrid columns={{ base: 1, md: 2 }} gap={3} alignItems="start">
        {/* As 3 com valor vêm primeiro (célula = switch + campo empilhado). */}
        <Box>
          <ScpSwitch name="dados_scp.confidencialidade" label="Confidencialidade" />
          {confid && (
            <Box mt={2}>
              <InputForm
                name="dados_scp.confidencialidade_multa"
                label="Multa de confidencialidade (R$)"
                type="number"
                controlMb={0}
              />
            </Box>
          )}
        </Box>
        <Box>
          <ScpSwitch
            name="dados_scp.limite_alcada"
            label="Limite de alçada"
            help="Valor acima do qual a participante precisa aprovar contratações."
          />
          {alcada && (
            <Box mt={2}>
              <InputForm
                name="dados_scp.limite_alcada_valor"
                label="Valor da alçada (R$)"
                type="number"
                controlMb={0}
              />
            </Box>
          )}
        </Box>
        <Box>
          <ScpSwitch name="dados_scp.mediacao_previa" label="Mediação prévia (Lei 13.140/15)" />
          {mediacao && (
            <Box mt={2}>
              <InputForm
                name="dados_scp.mediacao_camara"
                label="Câmara de mediação"
                controlMb={0}
              />
            </Box>
          )}
        </Box>
        {/* Sem valor — cada uma é uma célula própria. */}
        <ScpSwitch name="dados_scp.pro_labore" label="Pró-labore (remuneração dos sócios)" />
        <ScpSwitch name="dados_scp.acordo_socios" label="Acordo de sócios" />
        <ScpSwitch
          name="dados_scp.admissao_livre"
          label="Admitir sócio sem novo consentimento (art. 995)"
          help="Padrão (desligado): admissão exige consentimento expresso dos demais."
        />
        <ScpSwitch name="dados_scp.exclusao_retirada" label="Exclusão, retirada e falecimento" />
        <ScpSwitch
          name="dados_scp.obrigacoes_detalhadas"
          label="Obrigações detalhadas da ostensiva"
          help="Seguros, subcontratações, transparência com prazos (modo projeto específico)."
        />
        <ScpSwitch
          name="dados_scp.assinatura_detalhada"
          label="Declarações detalhadas de assinatura eletrônica"
        />
        <ScpSwitch
          name="dados_scp.testemunhas"
          label="Bloco de 2 testemunhas"
          help="Ligue na assinatura física (título executivo). Dispensável no eletrônico via provedor."
        />
      </SimpleGrid>
    </Box>
  );
};

export default ScpConfigPanel;
